import { useCallback, useState } from "react";
import { formatDateForApi } from "../dateFormatting";

export const CALENDAR_RANGE_STORAGE_KEY = "optivo-ui-pricing-calendar-view";

const CALENDAR_RANGE_PRESET_IDS = ["thisWeek", "nextWeek", "thisMonth", "next14Days", "next30Days"] as const;

export type CalendarRangePresetId = (typeof CALENDAR_RANGE_PRESET_IDS)[number];

export interface CalendarRangePreset {
    id: CalendarRangePresetId;
    from: string;
    to: string;
}

export type CalendarRangePreference =
    | { type: "preset"; presetId: CalendarRangePresetId }
    | { type: "custom"; fromDate: string; toDate: string };

export interface CalendarRangeState {
    fromDate: string;
    toDate: string;
    preference: CalendarRangePreference;
}

export interface CalendarSelectRangeOptions {
    preference?: CalendarRangePreference;
    persist?: boolean;
}

export type CalendarSelectRangeHandler = (from: string, to: string, options?: CalendarSelectRangeOptions) => void;

function toDateStr(d: Date): string {
    return formatDateForApi(d);
}

function isPresetId(value: unknown): value is CalendarRangePresetId {
    return typeof value === "string" && CALENDAR_RANGE_PRESET_IDS.includes(value as CalendarRangePresetId);
}

function isIsoDate(value: unknown): value is string {
    if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const parsed = new Date(`${value}T00:00:00`);
    return !Number.isNaN(parsed.getTime()) && toDateStr(parsed) === value;
}

function readStoredPreference(): CalendarRangePreference | null {
    if (typeof window === "undefined") return null;

    try {
        const raw = window.localStorage.getItem(CALENDAR_RANGE_STORAGE_KEY);
        if (!raw) return null;

        const parsed: unknown = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return null;

        if ("type" in parsed && parsed.type === "preset" && "presetId" in parsed && isPresetId(parsed.presetId)) {
            return { type: "preset", presetId: parsed.presetId };
        }

        if (
            "type" in parsed
            && parsed.type === "custom"
            && "fromDate" in parsed
            && "toDate" in parsed
            && isIsoDate(parsed.fromDate)
            && isIsoDate(parsed.toDate)
            && parsed.fromDate <= parsed.toDate
        ) {
            return { type: "custom", fromDate: parsed.fromDate, toDate: parsed.toDate };
        }
    } catch {
        return null;
    }

    return null;
}

function writeStoredPreference(preference: CalendarRangePreference): void {
    if (typeof window === "undefined") return;

    try {
        window.localStorage.setItem(CALENDAR_RANGE_STORAGE_KEY, JSON.stringify(preference));
    } catch {
        // Non-critical: the visible calendar range should still update if storage is unavailable.
    }
}

export function getCalendarRangePresets(referenceDate = new Date()): CalendarRangePreset[] {
    const today = new Date(referenceDate);
    const dow = today.getDay();
    const mondayOffset = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const nextMonday = new Date(sunday);
    nextMonday.setDate(sunday.getDate() + 1);
    const nextSunday = new Date(nextMonday);
    nextSunday.setDate(nextMonday.getDate() + 6);

    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    const next14 = new Date(today);
    next14.setDate(today.getDate() + 13);

    const next30 = new Date(today);
    next30.setDate(today.getDate() + 29);

    return [
        { id: "thisWeek", from: toDateStr(monday), to: toDateStr(sunday) },
        { id: "nextWeek", from: toDateStr(nextMonday), to: toDateStr(nextSunday) },
        { id: "thisMonth", from: toDateStr(monthStart), to: toDateStr(monthEnd) },
        { id: "next14Days", from: toDateStr(today), to: toDateStr(next14) },
        { id: "next30Days", from: toDateStr(today), to: toDateStr(next30) },
    ];
}

function resolvePreference(preference: CalendarRangePreference, referenceDate = new Date()): CalendarRangeState | null {
    if (preference.type === "custom") {
        if (!isIsoDate(preference.fromDate) || !isIsoDate(preference.toDate) || preference.fromDate > preference.toDate) {
            return null;
        }

        return {
            fromDate: preference.fromDate,
            toDate: preference.toDate,
            preference,
        };
    }

    const preset = getCalendarRangePresets(referenceDate).find((candidate) => candidate.id === preference.presetId);
    if (!preset) return null;

    return {
        fromDate: preset.from,
        toDate: preset.to,
        preference,
    };
}

function getDefaultCalendarRangeState(referenceDate = new Date()): CalendarRangeState {
    const preference: CalendarRangePreference = { type: "preset", presetId: "thisWeek" };
    const resolved = resolvePreference(preference, referenceDate);
    if (!resolved) {
        throw new Error("Default calendar range preset could not be resolved");
    }

    return resolved;
}

function spanDaysOf(fromDate: string, toDate: string): number {
    const from = new Date(fromDate + "T00:00:00");
    const to = new Date(toDate + "T00:00:00");
    return Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
}

/** The same number of days, re-anchored so today is the first visible day. */
function spanFromToday(spanDays: number, referenceDate = new Date()): CalendarRangeState {
    const newFrom = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());
    const newTo = new Date(newFrom);
    newTo.setDate(newFrom.getDate() + spanDays - 1);
    const fromDate = toDateStr(newFrom);
    const toDate = toDateStr(newTo);
    return { fromDate, toDate, preference: { type: "custom", fromDate, toDate } };
}

/**
 * The range the Prices page opens on: always one that shows today. A preset
 * is resolved against today (so "this month" is this month), and one that
 * still misses today ("next week") or an exact custom range keeps only its
 * length, opening at today — the dates a user wandered to on a previous
 * visit are not where they want to land on the next one.
 */
function rangeShowingToday(preference: CalendarRangePreference, referenceDate = new Date()): CalendarRangeState | null {
    const resolved = resolvePreference(preference, referenceDate);
    if (!resolved) return null;

    const today = toDateStr(referenceDate);
    if (resolved.preference.type === "preset" && resolved.fromDate <= today && today <= resolved.toDate) {
        return resolved;
    }

    return spanFromToday(spanDaysOf(resolved.fromDate, resolved.toDate), referenceDate);
}

export function getInitialCalendarRangeState(referenceDate = new Date()): CalendarRangeState {
    const storedPreference = readStoredPreference();
    if (storedPreference) {
        const resolved = rangeShowingToday(storedPreference, referenceDate);
        if (resolved) return resolved;
    }

    return getDefaultCalendarRangeState(referenceDate);
}

/**
 * Manages calendar date-range state.
 * Default range: current week (Mon–Sun).
 */
export function useCalendarNavigation() {
    const [rangeState, setRangeState] = useState(() => getInitialCalendarRangeState());
    const { fromDate, toDate, preference } = rangeState;

    // Calendar data loading state
    const [isLoadingCalendarData, setIsLoadingCalendarData] = useState(false);

    /** Set an arbitrary from/to range and trigger data load. */
    const handleSelectRange: CalendarSelectRangeHandler = useCallback((from, to, options) => {
        const nextPreference = options?.preference ?? { type: "custom", fromDate: from, toDate: to };
        setRangeState({ fromDate: from, toDate: to, preference: nextPreference });

        if (options?.persist !== false) {
            writeStoredPreference(nextPreference);
        }
    }, []);

    /** Shift by the current range span (prev/next). */
    const handleDateChange = useCallback((direction: "prev" | "next") => {
        const from = new Date(fromDate + "T00:00:00");
        const to = new Date(toDate + "T00:00:00");
        const spanDays = Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
        const shift = direction === "next" ? spanDays : -spanDays;
        const newFrom = new Date(from);
        newFrom.setDate(from.getDate() + shift);
        const newTo = new Date(to);
        newTo.setDate(to.getDate() + shift);
        const nextFromDate = toDateStr(newFrom);
        const nextToDate = toDateStr(newTo);
        const nextPreference: CalendarRangePreference = { type: "custom", fromDate: nextFromDate, toDate: nextToDate };
        setRangeState({ fromDate: nextFromDate, toDate: nextToDate, preference: nextPreference });
        writeStoredPreference(nextPreference);
    }, [fromDate, toDate]);

    /**
     * Jump to the current period: keep the selected range span (number of days)
     * and re-anchor it so today is the first visible day.
     */
    const handleJumpToToday = useCallback(() => {
        const next = spanFromToday(spanDaysOf(fromDate, toDate));
        setRangeState(next);
        writeStoredPreference(next.preference);
    }, [fromDate, toDate]);

    /**
     * Arriving on the Prices page from elsewhere: bring the range back to one
     * that shows today. Moving between the page's own tabs does not call this,
     * so the dates chosen there hold for as long as the user stays. It reads
     * the stored preference — the user's own choice — rather than the range on
     * screen, which a Recent Bookings stay may have borrowed without storing.
     */
    const handleEnterPrices = useCallback(() => {
        setRangeState(getInitialCalendarRangeState());
    }, []);

    return {
        fromDate,
        toDate,
        activeRangePreference: preference,
        isLoadingCalendarData,
        setIsLoadingCalendarData,
        handleSelectRange,
        handleDateChange,
        handleJumpToToday,
        handleEnterPrices,
    };
}
